/**
 * Unified Chart Theme Configuration
 * 
 * This module provides a consistent, modern glass-like styling system
 * for all Nivo charts across the dashboard. All charts should import
 * and use these configurations to maintain visual consistency.
 * 
 * Mac/Retina Optimization Notes:
 * - Font smoothing is applied via CSS for crisp text on Retina displays
 * - Font sizes are designed to scale well at 2x pixel density
 * - Use -webkit-font-smoothing: antialiased for Mac optimization
 */

import {
    color_defaults_white,
    color_shades_darkest,
    color_shades_darker,
    color_shades_dark,
    color_shades_light,
    color_shades_lighter,
    chart_blue_1,
    chart_blue_2,
    chart_blue_3,
    chart_blue_4,
    chart_blue_5,
    chart_blue_6,
    chart_blue_7,
    chart_blue_8,
    chart_blue_9,
    chart_blue_10,
    chart_blue_11,
    chart_blue_12,
    chart_glass_blue_1,
    chart_glass_blue_2,
    chart_glass_blue_3,
    chart_glass_blue_4,
    chart_glass_blue_5,
    chart_glass_blue_6,
    chart_glass_blue_7,
    chart_glass_blue_8,
    chart_glass_blue_9,
    chart_glass_blue_10,
    chart_glass_blue_11,
    chart_glass_blue_12,
    chartSequentialBlues,
    chartGlassBlues,
    chartBorderBlues
} from './colors';

// ==================== NIVO THEME ====================
// Base theme for all Nivo charts with modern, clean styling

export const nivoChartTheme = {
    background: 'transparent',
    textColor: color_shades_darkest,
    fontSize: 11,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    axis: {
        domain: {
            line: {
                stroke: color_shades_light,
                strokeWidth: 1
            }
        },
        ticks: {
            line: {
                stroke: color_shades_light,
                strokeWidth: 1
            },
            text: {
                fontSize: 11,
                fill: color_shades_darker,
                fontWeight: 500
            }
        },
        legend: {
            text: {
                fontSize: 11,
                fill: color_shades_darker,
                fontWeight: 600
            }
        }
    },
    grid: {
        line: {
            stroke: color_shades_light,
            strokeWidth: 1,
            strokeDasharray: '3 3'
        }
    },
    legends: {
        text: {
            fontSize: 11,
            fill: color_shades_darker,
            fontWeight: 500
        }
    },
    tooltip: {
        container: {
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(8px)',
            color: color_shades_darkest,
            fontSize: 12,
            borderRadius: '10px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.05)',
            padding: '10px 14px',
            border: `1px solid ${color_shades_light}`
        }
    },
    labels: {
        text: {
            fontSize: 11,
            fontWeight: 600,
            fill: color_defaults_white
        }
    }
};

// ==================== COLOR PALETTES ====================
// Sequential blue palette for bar charts, ordered data

export interface ChartColorConfig {
    fill: string;
    border: string;
    solid: string;
}

/**
 * Get a color configuration for a specific index
 * Returns fill (glass), border, and solid colors
 */
export const getChartColorConfig = (index: number): ChartColorConfig => {
    const safeIndex = index % chartSequentialBlues.length;
    return {
        fill: chartGlassBlues[safeIndex],
        border: chartBorderBlues[safeIndex],
        solid: chartSequentialBlues[safeIndex]
    };
};

/**
 * Generate an array of colors for N data points
 * Spreads colors evenly across the palette
 */
export const generateChartColors = (count: number, useGlass: boolean = true): string[] => {
    if (count <= 0) return [];

    const palette = useGlass ? chartGlassBlues : chartSequentialBlues;

    if (count >= palette.length) {
        // Use all colors
        return [...palette];
    }

    // Spread colors evenly across the palette
    const colors: string[] = [];
    const step = (palette.length - 1) / (count - 1 || 1);

    for (let i = 0; i < count; i++) {
        const index = Math.round(i * step);
        colors.push(palette[index]);
    }

    return colors;
};

/**
 * Get paired fill and border colors for glass effect
 * @param count - Number of color pairs to generate
 * @param maxPaletteIndex - Optional max index to limit how light colors can be (0-11, default 11)
 */
export const generateGlassColorPairs = (count: number, maxPaletteIndex: number = 11): Array<{ fill: string; border: string }> => {
    if (count <= 0) return [];

    // Clamp maxPaletteIndex to valid range
    const maxIdx = Math.min(Math.max(maxPaletteIndex, 0), chartGlassBlues.length - 1);

    const pairs: Array<{ fill: string; border: string }> = [];
    const step = maxIdx / (count - 1 || 1);

    for (let i = 0; i < count; i++) {
        const index = Math.round(i * step);
        pairs.push({
            fill: chartGlassBlues[index],
            border: chartBorderBlues[index]
        });
    }

    return pairs;
};

// ==================== BAR CHART CONFIG ====================
// Default configuration for bar charts

export const barChartDefaults = {
    borderRadius: 4,
    borderWidth: 2,
    padding: 0.3,
    innerPadding: 0,
    labelSkipWidth: 32,
    labelSkipHeight: 12,
    labelTextColor: color_defaults_white,
    enableGridX: true,
    enableGridY: false,
    animate: true,
    motionConfig: 'gentle' as const
};

// ==================== FUNNEL CHART CONFIG ====================
// Default configuration for funnel charts

export const funnelChartDefaults = {
    borderWidth: 20,
    currentPartSizeExtension: 10,
    currentBorderWidth: 20,
    beforeSeparatorLength: 100,
    beforeSeparatorOffset: 20,
    afterSeparatorLength: 100,
    afterSeparatorOffset: 20,
    animate: true,
    motionConfig: 'gentle' as const
};

// Custom funnel colors (dark to light for conversion visualization)
export const funnelColors = [
    chart_blue_1,   // Total calls - darkest
    chart_blue_4,   // Engaged calls - medium
    chart_blue_7    // Orders placed - lighter
];

export const funnelGlassColors = [
    chart_glass_blue_1,
    chart_glass_blue_4,
    chart_glass_blue_7
];

// ==================== PIE CHART CONFIG ====================
// Default configuration for pie charts

export const pieChartDefaults = {
    innerRadius: 0.5,
    padAngle: 2,
    cornerRadius: 4,
    activeOuterRadiusOffset: 8,
    borderWidth: 2,
    enableArcLinkLabels: false,
    arcLabelsSkipAngle: 15,
    arcLabelsTextColor: color_defaults_white,
    animate: true,
    motionConfig: 'gentle' as const
};

// ==================== TOOLTIP STYLES ====================
// Shared tooltip styling for consistency

export const tooltipStyles = {
    container: {
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(8px)',
        padding: '12px 16px',
        borderRadius: '10px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.05)',
        border: `1px solid ${color_shades_light}`,
        minWidth: '180px'
    },
    title: {
        fontWeight: 600,
        marginBottom: 8,
        color: color_shades_darkest,
        fontSize: '14px'
    },
    value: {
        fontWeight: 600,
        color: color_shades_darkest,
        fontSize: '18px'
    },
    subtitle: {
        color: color_shades_darker,
        fontSize: '12px'
    },
    colorIndicator: {
        width: '12px',
        height: '12px',
        borderRadius: '3px',
        border: '1px solid rgba(0, 0, 0, 0.1)'
    }
};

// ==================== DASHBOARD TILE STYLES ====================
// Shared styles for dashboard chart tiles with consistent hover effects

/**
 * Base tile styles - use these as the foundation for all dashboard tiles
 * Provides consistent appearance and hover animation across all charts
 */
export const dashboardTileStyles = {
    base: {
        backgroundColor: color_defaults_white,
        border: 'none',
        borderRadius: '16px',
        padding: '16px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
        transition: 'box-shadow 0.2s ease',
    },
    hover: {
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
    },
    mobile: {
        padding: '12px',
        minHeight: '250px',
    }
};

/**
 * CSS string for base tile - can be used with styled-components or emotion
 * Includes the hover effect that should be applied to all dashboard tiles
 */
export const baseTileCSS = `
    background-color: ${color_defaults_white};
    border: none;
    border-radius: 16px;
    padding: 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    transition: box-shadow 0.2s ease;
    overflow: visible;
    display: flex;
    flex-direction: column;
    min-height: 0;
    position: relative;

    &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    }
`;

/**
 * Tile size configurations for different chart types
 */
export const tileSizeConfig = {
    // Standard chart tile (half width)
    chart: {
        gridColumn: 'span 6',
        gridRow: 'span 1',
        mobileMinHeight: '280px',
    },
    // Wide chart tile (full width) - for histograms
    wide: {
        gridColumn: 'span 12',
        gridRow: 'span 1',
        mobileMinHeight: '280px',
    },
    // Tall chart tile (spans 2 rows) - for vertical charts like transfers
    tall: {
        gridColumn: '7 / span 6',
        gridRow: '1 / span 2',
        mobileMinHeight: '380px',
    },
    // Number tile (1/3 width)
    number: {
        gridColumn: 'span 4',
        gridRow: 'span 1',
        mobileMinHeight: '150px',
    },
    // Small number tile (1/6 width)
    smallNumber: {
        gridColumn: 'span 2',
        gridRow: 'span 1',
        mobileMinHeight: '150px',
    }
};

// ==================== EXPORTS FOR DIRECT PALETTE ACCESS ====================

export {
    chartSequentialBlues,
    chartGlassBlues,
    chartBorderBlues,
    chart_blue_1,
    chart_blue_2,
    chart_blue_3,
    chart_blue_4,
    chart_blue_5,
    chart_blue_6,
    chart_blue_7,
    chart_blue_8,
    chart_blue_9,
    chart_blue_10,
    chart_blue_11,
    chart_blue_12
};
